import { FC, useEffect, useState } from "react";
import { Col, Form, FormFeedback, FormGroup, Label, Row } from "reactstrap";
import ModalComponent from "../../shared/base-components/Modal";
import TextField from "../../shared/base-components/TextField";
import { SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import Button from "../../shared/base-components/Button";
import DatePicker from "../../shared/base-components/DatePicker";
import {
  StaffModalProps,
  staffAddSchema,
  staffUpdateSchema,
} from "./Staff.constants";
import { useStaffAddMutation, useStaffUpdateMutation } from "./Staff.actions";
import { MODAL_TYPES, RESPONSE_CODE } from "../../shared/models/Types";
import { successNotify } from "../../shared/utils/notifications";
import Switch from "../../shared/base-components/Switch";

const StaffModal: FC<StaffModalProps> = ({
  userData,
  isModalOpen,
  modalType,
  toggle,
  mutate,
}) => {
  const isEdit = modalType === MODAL_TYPES.UPDATE;

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<any>({
    resolver: yupResolver(isEdit ? staffUpdateSchema : staffAddSchema),
  });

  const { data, mutate: staffAddMutate, isLoading } = useStaffAddMutation();
  const {
    data: updateData,
    mutate: staffUpdate,
    isLoading: updateLoading,
  } = useStaffUpdateMutation();

  const [isFollow, setIsFollow] = useState<boolean>(
    userData?.can_follow || false
  );

  const onSubmit: SubmitHandler<any> = (requestValues) => {
    requestValues.can_follow = isFollow;

    isEdit
      ? staffUpdate({ ...requestValues, id: userData?.id })
      : staffAddMutate(requestValues);
  };

  useEffect(() => {
    if (data?.status === RESPONSE_CODE.CREATED) {
      successNotify("Personel başarılı bir şekilde oluşturuldu");
      toggle();

      mutate && mutate({});
    }
  }, [data]);

  useEffect(() => {
    if (updateData?.status === RESPONSE_CODE.SUCCESS_CODE) {
      successNotify("Personel başarılı bir şekilde güncellendi");
      toggle();

      mutate && mutate({});
    }
  }, [updateData]);

  return (
    <ModalComponent
      header={isEdit ? "Personel Güncelle" : "Personel Ekle"}
      description={
        isEdit
          ? "Personel Güncellemek için ilgili alanı düzeltip onaylayın"
          : "Personel eklemek için gerekli alanları doldurunuz."
      }
      isOpen={isModalOpen}
      toggle={toggle}
    >
      <Form onSubmit={handleSubmit(onSubmit)} className="custom-form mt-4 pt-2">
        <Row>
          <Col sm="12" md="6" lg="6">
            <FormGroup className="mb-3">
              <Label className="form-label">Email</Label>
              <TextField
                control={control}
                name="email"
                defaultValue={isEdit ? userData?.email : ""}
                type="text"
                className="form-control"
                placeholder="Email"
                error={errors.email?.message}
              />
            </FormGroup>
          </Col>
          <Col sm="12" md="6" lg="6">
            <FormGroup className="mb-3">
              <Label htmlFor="validationCustom02">Şifre</Label>
              <TextField
                control={control}
                name="password"
                className="form-control"
                type="password"
                placeholder="Şifre"
                error={errors.password?.message}
              />
            </FormGroup>
          </Col>
          <Col sm="12" md="6" lg="6">
            <FormGroup className="mb-3">
              <Label className="form-label">Tam Ad</Label>
              <TextField
                control={control}
                name="fullname"
                type="text"
                className="form-control"
                defaultValue={isEdit ? userData?.fullname : ""}
                placeholder="Tam Ad"
                error={errors.fullname?.message}
              />
            </FormGroup>
          </Col>
          <Col sm="12" md="6" lg="6">
            <FormGroup className="mb-3">
              <Label htmlFor="validationCustom02">Kullanıcı adı</Label>
              <TextField
                control={control}
                name="username"
                className="form-control"
                defaultValue={isEdit ? userData?.username : ""}
                type="text"
                placeholder="Kullanıcı Adı"
                error={errors.username?.message}
              />
            </FormGroup>
          </Col>
          <Col sm="12" md="6" lg="6">
            <FormGroup className="mb-3">
              <Label className="form-label">Başlangıç Tarihi</Label>
              <DatePicker
                control={control}
                name="date_of_start"
                type="text"
                defaultValue={isEdit ? userData?.date_of_start : new Date()}
                className="form-control"
                placeholder="Başlangıç Tarihi"
                error={errors.date_of_start?.message}
              />
            </FormGroup>
          </Col>
          <Col sm="12" md="6" lg="6">
            <FormGroup className="mb-3">
              <Label htmlFor="validationCustom02">Telefon Numarası</Label>
              <TextField
                control={control}
                name="phone_number"
                className="form-control"
                type="text"
                defaultValue={isEdit ? userData?.phone_number : ""}
                placeholder="0--- --- -- --"
                error={errors.phone_number?.message}
              />
            </FormGroup>
          </Col>
          <Col sm="12" md="6" lg="6">
            <FormGroup className="mb-3">
              <Label className="form-label">Net Maaş</Label>
              <TextField
                control={control}
                name="salary_net"
                defaultValue={isEdit ? userData?.salary_net.toString() : 0}
                type="text"
                className="form-control"
                placeholder="Net Maaş"
                error={errors.salary_net?.message}
              />
            </FormGroup>
          </Col>
          <Col sm="12" md="6" lg="6">
            <FormGroup className="mb-3">
              <Label htmlFor="validationCustom02">Brüt Maaş</Label>
              <TextField
                control={control}
                name="salary_gross"
                className="form-control"
                defaultValue={isEdit ? userData?.salary_gross.toString() : 0}
                type="text"
                placeholder="Brüt Maaş"
                error={errors.salary_gross?.message}
              />
            </FormGroup>
          </Col>

          <Col sm="12" md="6" lg="6">
            <Label htmlFor="validationCustom02">Konum Takip</Label>
            <FormGroup className="mb-3">
              <Switch
                onChangeHandler={(e: any) => setIsFollow(e)}
                name="can_follow"
                control={control}
                dataOnLabel="Var"
                dataOffLabel="Yok"
                color="success"
                defaultValue={userData?.can_follow}
              />
            </FormGroup>
          </Col>

          <Col md="12">
            <FormGroup className="mb-3">
              <h6 className="text-danger">
                {isEdit
                  ? updateData?.status === RESPONSE_CODE.SERVER_INVALID &&
                    (updateData?.hasOwnProperty("email")
                      ? "Bu email adresi kullanılmaktadır."
                      : updateData?.username)
                  : data?.status === RESPONSE_CODE.SERVER_INVALID &&
                    (data?.hasOwnProperty("email")
                      ? "Bu email adresi kullanılmaktadır."
                      : data?.username)}
              </h6>
              <Button
                loading={isEdit ? updateLoading : isLoading}
                text={isEdit ? "Personel Güncelle" : "Personel Ekle"}
                variant="primary"
              />
            </FormGroup>
          </Col>
        </Row>
      </Form>
    </ModalComponent>
  );
};

export default StaffModal;
